import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/navigation';

import { revalidate, setKioskGuestAuthCookies } from '@/app/actions';

import useLocalization from '@/hooks/useLocalization';
import { useKioskMode } from '@/hooks/useKioskMode';

import { GuestIdentityDTO, ReservationOption } from '@/models';
import { GuestPortalService } from '@/services/GuestPortal';
import { ClientAuthService, ClientGuestPortalService } from '@/services/client';
import { ClientCookiesStore } from '@/services/cookies/client';
import { getKioskRedirectAction } from './kioskRedirect';
import { LOGIN_STEP, LoginState, UseLoginReturn } from './types';

export function useKioskLogin({
  propertyId,
  redirect,
}: {
  propertyId: string;
  redirect?: string;
}): UseLoginReturn {
  const [state, setState] = useState<LoginState>({
    reservationId: '',
    lastName: '',
    firstName: '',
    arrivalDate: '',
    reservationNotFound: false,
    guestNotFound: false,
    loading: false,
    errorText: '',
    submittedFields: [],
    currentStep: LOGIN_STEP.LASTNAME,
    matchingReservations: [],
  });
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocalization();
  const { setSearchCriteria, getSearchCriteria } = useKioskMode();

  const {
    reservationId,
    lastName,
    firstName,
    arrivalDate,
    reservationNotFound,
    loading,
    submittedFields,
    matchingReservations,
  } = state;

  const setStateField = useCallback(
    <K extends keyof LoginState>(field: K, value: LoginState[K]) => {
      setState((s) => ({
        ...s,
        [field]: value,
      }));
    },
    [setState],
  );

  const authenticateAndRedirect = useCallback(
    async (
      response: GuestIdentityDTO,
      reservation?: ReservationOption,
      flowType?: 'check-in' | 'check-out',
    ) => {
      const authData = await GuestPortalService.generateGuestJWT({
        propertyId,
        token: response.token,
      });

      // BOOK-7604: set guest auth cookies server-side (Set-Cookie) instead of
      // client-side document.cookie. Inside the iOS kiosk WKWebView the latter
      // is not reliably committed before the redirect server action / the
      // navigation to the authenticated route read it, which bounced the guest
      // back to /login in a loop under Guided Access. Awaiting this guarantees
      // the cookies are committed before the requests below.
      await setKioskGuestAuthCookies(authData);

      if (redirect) {
        await revalidate(redirect);
        router.push(decodeURIComponent(redirect));
        return;
      }

      const guestBookSlug = ClientCookiesStore.getGuestBookSlug() || 'guest';

      const reservationData = response.reservation ?? reservation;

      const kioskRedirectPath = await getKioskRedirectAction({
        locale,
        propertyId,
        guestBookSlug,
        reservation: reservationData,
        flowType,
      });

      router.push(kioskRedirectPath);
    },
    [locale, propertyId, redirect, router],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const submittedFields = [
        lastName && 'lastName',
        firstName && 'firstName',
        arrivalDate && 'arrivalDate',
        reservationId && 'reservationId',
      ].filter(Boolean) as string[];

      setStateField('loading', true);

      try {
        await ClientAuthService.isKioskAuthenticated(propertyId);

        const response = await ClientGuestPortalService.kioskIdentifyGuest({
          propertyId,
          lastName,
          firstName: firstName || undefined,
          arrivalDate: arrivalDate || undefined,
          reservationId: reservationId || undefined,
        });

        if ('type' in response && response.type === 'reservation-not-found') {
          return setState((state) => ({
            ...state,
            reservationNotFound: submittedFields.includes('reservationId'),
            guestNotFound: false,
            loading: false,
            submittedFields,
            matchingReservations: [],
          }));
        }

        if ('type' in response && response.type === 'guest-not-found') {
          return setState((state) => ({
            ...state,
            reservationNotFound: false,
            guestNotFound:
              submittedFields.includes('reservationId') ||
              submittedFields.includes('arrivalDate'),
            loading: false,
            submittedFields,
            matchingReservations: [],
          }));
        }

        if ('type' in response && response.type === 'multiple-guests-found') {
          setSearchCriteria({
            lastName,
            firstName: firstName || '',
            arrivalDate: arrivalDate || '',
            totalReservationsFound: response.reservations.length,
          });
          return setState((state) => ({
            ...state,
            reservationNotFound: false,
            guestNotFound: false,
            loading: false,
            submittedFields,
            matchingReservations: response.reservations,
          }));
        }

        if ('guestId' in response) {
          await authenticateAndRedirect(response);
        }
      } catch (e) {
        console.error(e);

        setState((state) => ({
          ...state,
          loading: false,
          submittedFields,
          errorText: intl.formatMessage({
            id: 'something_went_wrong',
            defaultMessage: 'Oops! Something went wrong',
          }),
        }));
      }
    },
    [
      intl,
      propertyId,
      reservationId,
      lastName,
      firstName,
      arrivalDate,
      setStateField,
      authenticateAndRedirect,
      setSearchCriteria,
    ],
  );

  const isLastNameStep = !submittedFields.includes('lastName');
  const isFirstNameStep =
    !isLastNameStep && !submittedFields.includes('firstName');
  const isArrivalDateStep =
    !isLastNameStep &&
    !isFirstNameStep &&
    !submittedFields.includes('arrivalDate');
  const isSelectReservationStep =
    matchingReservations.length > 0 &&
    !reservationNotFound &&
    submittedFields.includes('lastName') &&
    submittedFields.includes('firstName') &&
    submittedFields.includes('arrivalDate');
  const isReservationStep =
    ((submittedFields.includes('lastName') &&
      submittedFields.includes('firstName') &&
      submittedFields.includes('arrivalDate') &&
      !submittedFields.includes('reservationId')) ||
      reservationNotFound) &&
    !isSelectReservationStep;

  const isDisabled =
    loading ||
    (isLastNameStep && !lastName) ||
    (isFirstNameStep && !firstName) ||
    (isArrivalDateStep && !arrivalDate) ||
    (isReservationStep && !reservationId) ||
    isSelectReservationStep;

  const canGoBack = submittedFields.length > 0 && !isLastNameStep;
  const currentStep = isSelectReservationStep
    ? LOGIN_STEP.SELECT_RESERVATION
    : isLastNameStep
      ? LOGIN_STEP.LASTNAME
      : isFirstNameStep
        ? LOGIN_STEP.FIRSTNAME
        : isArrivalDateStep
          ? LOGIN_STEP.ARRIVALDATE
          : isReservationStep
            ? LOGIN_STEP.RESERVATIONID
            : LOGIN_STEP.LASTNAME;

  const onBack = useCallback(() => {
    setState((state) => ({
      ...state,
      submittedFields: state.submittedFields.slice(
        0,
        reservationNotFound ? -2 : -1,
      ),
      reservationNotFound: false,
      guestNotFound: false,
      matchingReservations: [],
      lastName: isLastNameStep ? '' : state.lastName,
      firstName: isFirstNameStep ? '' : state.firstName,
      arrivalDate: isArrivalDateStep ? '' : state.arrivalDate,
      reservationId: isReservationStep ? '' : state.reservationId,
    }));
  }, [
    reservationNotFound,
    isLastNameStep,
    isFirstNameStep,
    isArrivalDateStep,
    isReservationStep,
  ]);

  const onSelectReservation = useCallback(
    async (
      selectedReservation: ReservationOption,
      flowType?: 'check-in' | 'check-out',
    ) => {
      setState((s) => ({ ...s, loading: true }));

      try {
        const searchCriteria = getSearchCriteria();
        const criteria = searchCriteria || {
          lastName,
          firstName: firstName || undefined,
          arrivalDate: arrivalDate || undefined,
        };

        ClientAuthService.kioskLogout({ preserveSearchCriteria: true });

        await ClientAuthService.isKioskAuthenticated(propertyId);

        const response = await ClientGuestPortalService.kioskIdentifyGuest({
          propertyId,
          lastName: criteria.lastName,
          firstName: criteria.firstName || undefined,
          arrivalDate: criteria.arrivalDate || undefined,
          reservationId: selectedReservation.reservationId,
        });

        if ('guestId' in response) {
          await authenticateAndRedirect(
            response,
            selectedReservation,
            flowType,
          );
        }
      } catch (e) {
        console.error(e);

        setState((state) => ({
          ...state,
          loading: false,
          errorText: intl.formatMessage({
            id: 'something_went_wrong',
            defaultMessage: 'Oops! Something went wrong',
          }),
        }));
      }
    },
    [
      intl,
      propertyId,
      lastName,
      firstName,
      arrivalDate,
      authenticateAndRedirect,
      getSearchCriteria,
    ],
  );

  return {
    state,
    setState,
    setStateField,
    isSubmitDisabled: isDisabled,
    onSubmit,
    onBack,
    canGoBack,
    currentStep,
    onSelectReservation,
  };
}
